import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";

type SelectAllOption = {
	value: string | number;
	count?: number;
};

type SelectAllFacet = {
	checked: boolean | "indeterminate";
	toggle: () => void;
	count: number;
};

type FilterValues = Partial<Record<string, string[]>>;

const isSelectable = (option: SelectAllOption) => option.count !== 0;

const toCheckedState = (
	selectedCount: number,
	selectableCount: number
): boolean | "indeterminate" => {
	if (selectedCount === 0) {
		return false;
	}

	if (selectedCount === selectableCount) {
		return true;
	}

	return "indeterminate";
};

/**
 * Drives the "select all" row of a searchable multi-select facet.
 *
 * Its scope is limited to the currently filtered options: toggling adds or
 * removes only those values and leaves selections made outside the active
 * search untouched.
 *
 * @param filterId Formik field name holding the array of selected values.
 * @param options Filtered options currently in scope.
 */
export const useSelectAllFacet = (filterId: string, options: SelectAllOption[]): SelectAllFacet => {
	const { values, setFieldValue } = useFormikContext<FilterValues>();

	const selectableValues = useMemo(
		() => options.filter(isSelectable).map(option => String(option.value)),
		[options]
	);

	const selection = useMemo(() => new Set(values[filterId] ?? []), [values, filterId]);

	const selectedCount = selectableValues.filter(value => selection.has(value)).length;
	const areAllSelected = selectedCount === selectableValues.length;

	const toggle = useCallback(() => {
		const nextSelection = new Set(selection);

		if (areAllSelected) {
			selectableValues.forEach(value => nextSelection.delete(value));
		} else {
			selectableValues.forEach(value => nextSelection.add(value));
		}

		setFieldValue(filterId, Array.from(nextSelection));
	}, [areAllSelected, filterId, selectableValues, selection, setFieldValue]);

	return {
		checked: toCheckedState(selectedCount, selectableValues.length),
		toggle,
		count: selectableValues.length,
	};
};
